import { CHART_IDS } from '../charts/chartIds'

function compareAxisValues(a, b) {
  if (a instanceof Date && b instanceof Date) {
    return a.valueOf() - b.valueOf()
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b
  }

  const aString = String(a)
  const bString = String(b)
  if (!Number.isNaN(Date.parse(aString)) && !Number.isNaN(Date.parse(bString))) {
    return Date.parse(aString) - Date.parse(bString)
  }

  return aString.localeCompare(bString)
}

function parseTranslate(transform = '') {
  const match = /translate\(\s*([^\s,)]+)(?:[ ,]\s*([^\s,)]+))?\s*\)/.exec(transform)
  if (!match) return { x: 0, y: 0 }
  return {
    x: Number.parseFloat(match[1]) || 0,
    y: Number.parseFloat(match[2]) || 0,
  }
}

function getCellLabel(cell) {
  const textNode = cell.querySelector('text')
  return textNode?.textContent?.trim() || ''
}

export function getBumpLegendOrder(mappedData = [], mapping = {}) {
  const rankIds = Array.isArray(mapping?.rank?.ids) ? mapping.rank.ids : []
  const rankValue = Array.isArray(mapping?.rank?.value)
    ? mapping.rank.value
    : mapping?.rank?.value

  const hasMappedRank = rankIds.length > 0 || (Array.isArray(rankValue) ? rankValue.length > 0 : Boolean(rankValue))

  if (!Array.isArray(mappedData) || !mappedData.length || !hasMappedRank) {
    return null
  }

  const firstX = [...mappedData]
    .map((row) => row.x)
    .sort(compareAxisValues)[0]

  const firstRows = mappedData.filter((row) => compareAxisValues(row.x, firstX) === 0)
  if (!firstRows.length) return null

  const byStream = new Map()
  firstRows.forEach((row) => {
    const key = String(row.streams)
    if (!byStream.has(key)) {
      byStream.set(key, [])
    }
    byStream.get(key).push(row)
  })

  return Array.from(byStream.entries())
    .map(([stream, rows]) => ({
      stream,
      rank:
        rows.reduce((sum, row) => sum + (Number(row.rank) || 0), 0) / rows.length,
    }))
    .sort((a, b) => b.rank - a.rank)
    .map((item) => item.stream)
}

export function reorderLegendItems(svgNode, { chartId, legendOrder } = {}) {
  if (!svgNode || chartId !== CHART_IDS.BUMP_CHART || !legendOrder?.length) return

  const legendGroup = svgNode.querySelector('#legend .legendColor')
  if (!legendGroup) return

  const cells = Array.from(legendGroup.querySelectorAll('.cell'))
  if (!cells.length) return

  const indexByLabel = new Map(legendOrder.map((label, index) => [String(label), index]))
  const sortedCells = cells
    .map((cell, originalIndex) => ({
      cell,
      originalIndex,
      label: getCellLabel(cell),
      translate: parseTranslate(cell.getAttribute('transform') || ''),
      height: (() => {
        try {
          return cell.getBBox().height || 14
        } catch (error) {
          return 14
        }
      })(),
    }))
    .sort((a, b) => {
      const aIndex = indexByLabel.has(a.label) ? indexByLabel.get(a.label) : Number.POSITIVE_INFINITY
      const bIndex = indexByLabel.has(b.label) ? indexByLabel.get(b.label) : Number.POSITIVE_INFINITY
      if (aIndex !== bIndex) return aIndex - bIndex
      return a.originalIndex - b.originalIndex
    })

  const originalYs = sortedCells.map((item) => item.translate.y).sort((a, b) => a - b)
  const defaultStep = sortedCells.length > 1
    ? Math.max(
        16,
        originalYs.slice(1).reduce((min, y, index) => {
          const delta = y - originalYs[index]
          return delta > 0 ? Math.min(min, delta) : min
        }, Number.POSITIVE_INFINITY)
      )
    : 18

  sortedCells.forEach((item, index) => {
    const nextY =
      Number.isFinite(originalYs[index]) ? originalYs[index] : index * defaultStep
    item.cell.setAttribute('transform', `translate(${item.translate.x},${nextY})`)
    legendGroup.appendChild(item.cell)
  })
}
